/**
 * Identify the authenticated user in PostHog (Cognito sub + person properties).
 * Use after login or when the app loads with an existing session so engagement is user-specific.
 *
 * No-ops when:
 * - The user is in the Admin group (never creates/updates a PostHog person for admins).
 * - The user has not consented to tracking via userConsent.
 */

import { hasTrackingConsent } from "./userConsent";

export interface IdentifyUserForPostHogOptions {
  email?: string;
  name?: string;
  isAdmin?: boolean;
  authProvider?: string;
  userConsentRaw?: string | null;
}

export function identifyUserForPostHog(
  posthog: { identify: (distinctId: string, personProperties?: Record<string, unknown>) => void } | undefined,
  sub: string,
  options: IdentifyUserForPostHogOptions
): void {
  if (!posthog || options.isAdmin) {
    return;
  }

  // Gate on user tracking consent
  if (!hasTrackingConsent(options.userConsentRaw)) {
    return;
  }

  const personProperties: Record<string, unknown> = {};
  if (options.email !== undefined) personProperties.email = options.email;
  if (options.name !== undefined) personProperties.name = options.name;
  if (options.authProvider !== undefined) personProperties.auth_provider = options.authProvider;
  posthog.identify(sub, Object.keys(personProperties).length > 0 ? personProperties : undefined);
}
